if (!customElements.get('free-shipping')) {
  class FreeShipping extends HTMLElement {
    async connectedCallback() {
      this.animateRewardsProgress();
      const { cartTotal, minimum, digitalOnly, bundleFreeShipping } = this.dataset;
      const total = parseInt(cartTotal, 10);
      const minRequired = parseInt(minimum, 10);
      // Rein digitale Warenkörbe (z. B. nur ein Gutschein) werden nicht
      // versendet – dort gilt kostenloser Versand unabhängig vom Warenwert.
      const isDigitalOnly = digitalOnly === 'true';
      // Bundles mit aktiviertem Gratisversand bekommen den Versand im Checkout
      // per Shopify Function erlassen – ebenfalls unabhängig vom Warenwert.
      const hasBundleFreeShipping = bundleFreeShipping === 'true';
      let isBelowMinimum = isDigitalOnly || hasBundleFreeShipping ? false : total < minRequired;
      const remaining = minRequired - total;

      let percentage = isBelowMinimum ? total / minRequired : 1;

      // Get the last known percentage to avoid starting from 0 every time
      const lastPercentage = window._lastFreeShippingPercentage || 0;

      // Only start from 0 if there's no previous state
      if (lastPercentage === 0) {
        this.style.setProperty('--percentage', 0);
      } else {
        // Start from the last known percentage to make updates smoother
        this.style.setProperty('--percentage', lastPercentage);
      }

      this.toggleVisibility(isBelowMinimum);
      this.updateRemainingAmount(isBelowMinimum, remaining);

      window._lastFreeShippingPercentage = percentage;

      // Set actual percentage in next frame for animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.style.setProperty('--percentage', percentage);
        });
      });

      if (typeof window.checkDiscountMetaobject === 'function') {
        try {
          const matchFound = await window.checkDiscountMetaobject();
          if (matchFound === true) {
            isBelowMinimum = false;
            this.toggleVisibility(isBelowMinimum);
            window._lastFreeShippingPercentage = 1;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.style.setProperty('--percentage', 1);
              });
            });
          }
        } catch (error) {
          console.error('Error checking discount metaobject:', error);
        }
      }
    }

    animateRewardsProgress() {
      const rewards = this.querySelector('.cart-rewards');
      const fill = rewards?.querySelector('.cart-rewards__fill');
      if (!fill) {
        FreeShipping.rewardsProgress = undefined;
        return;
      }

      const target = parseFloat(rewards.style.getPropertyValue('--rewards-progress'));
      if (!Number.isFinite(target)) return;

      const previous = FreeShipping.rewardsProgress;
      FreeShipping.rewardsProgress = target;
      if (
        !Number.isFinite(previous) ||
        previous === target ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        typeof fill.animate !== 'function'
      ) {
        return;
      }

      this.rewardsAnimationStart = previous;
      this.rewardsAnimationTarget = target;
      this.rewardsAnimation = fill.animate([{ width: `${previous}%` }, { width: `${target}%` }], {
        duration: 650,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      });
    }

    disconnectedCallback() {
      if (!this.rewardsAnimation) return;

      // Preserve the visible position when another cart render interrupts the animation.
      const progress =
        this.rewardsAnimation.playState === 'finished'
          ? 1
          : (this.rewardsAnimation.effect.getComputedTiming().progress ?? 0);
      FreeShipping.rewardsProgress =
        this.rewardsAnimationStart +
        (this.rewardsAnimationTarget - this.rewardsAnimationStart) * progress;
      this.rewardsAnimation.cancel();
      this.rewardsAnimation = null;
    }

    toggleVisibility(isBelowMinimum) {
      this.classList.toggle('free-shipping--reached', !isBelowMinimum);
      const shippingRemaining = this.querySelector('[data-rewards-shipping-remaining]');
      const bonusMessage = this.querySelector('[data-rewards-bonus-message]');
      if (shippingRemaining) shippingRemaining.hidden = !isBelowMinimum;
      if (bonusMessage) bonusMessage.hidden = isBelowMinimum;
      const rewardsBanner = this.querySelector('[data-rewards-complete]');
      if (rewardsBanner) {
        rewardsBanner.classList.toggle(
          'cart-rewards__message--success',
          !isBelowMinimum && rewardsBanner.dataset.rewardsComplete === 'true'
        );
      }
      this.querySelector('.cart-rewards__milestone--shipping')?.setAttribute(
        'aria-label',
        isBelowMinimum ? 'Gratisversand noch nicht erreicht' : 'Gratisversand erreicht'
      );
      this.querySelector('.free-shipping--text-remaining').style.display = isBelowMinimum
        ? 'block'
        : 'none';

      this.querySelector('.free-shipping--text-full').style.display = isBelowMinimum
        ? 'none'
        : 'block';

      this.querySelector('.free-shipping--bar')?.classList.toggle(
        'free-shipping--active',
        !isBelowMinimum
      );
    }

    updateRemainingAmount(isBelowMinimum, remaining) {
      if (isBelowMinimum) {
        const format = window.theme?.settings?.money_with_currency_format || '${{amount}}';
        this.querySelector('.free-shipping--text span').innerHTML = formatMoney(remaining, format);
        const rewardsAmount = this.querySelector('[data-rewards-shipping-amount]');
        if (rewardsAmount) rewardsAmount.innerHTML = formatMoney(remaining, format);
      }
    }
  }

  customElements.define('free-shipping', FreeShipping);
}
